import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Invoice, InvoiceStatus, InvoiceFilters } from '../../types/invoiceTypes';
import invoiceService from '../../services/invoiceService';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import InvoiceForm from '../../components/Invoices/InvoiceForm';
import { formatDateTime, formatCurrency, formatDate, formatDateTimeDMY } from '../../utils/helpers';
import styles from './InvoiceList.module.scss';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const InvoiceList: React.FC = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  // const [filters, setFilters] = useState<InvoiceFilters>({
  //   search: '',
  //   status: undefined,
  //   from_date: '',
  //   to_date: '',
  //   date: ""
  // });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);


  // Check permissions
  // const canManageInvoices = auth.user?.role === 'admin' || 
  //   auth.user?.permissions?.canManageInvoices;
  // const canDeleteInvoices = auth.user?.role === 'admin';

  const todayStr = new Date().toLocaleDateString('en-CA').slice(0, 10);
  const [filters, setFilters] = useState<InvoiceFilters>({
    search: '',
    status: undefined,
    from_date: '',
    to_date: '',
    date: todayStr
  });

  const loadInvoices = async (dateOverride?: string) => {
    setLoading(true);
    try {
      const date = dateOverride || filters.date || todayStr;
      const invoices = await invoiceService.getInvoicesByDate(date);
      setAllInvoices(Array.isArray(invoices) ? invoices : []);
      setFilteredInvoices(Array.isArray(invoices) ? invoices : []);
      setTotalPages(1); // Or calculate if you want to paginate
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  };
  

  // Load initial data
  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line
  }, []);

  // Filter invoices when filters change (except date, which is now handled by API)
  useEffect(() => {
    let filtered = allInvoices;

    // Apply search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(invoice =>
        invoice.invoice_number.toLowerCase().includes(searchTerm) ||
        invoice.customer_name.toLowerCase().includes(searchTerm)
      );
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(invoice => invoice.status === filters.status);
    }

    setFilteredInvoices(filtered);
  }, [filters.search, filters.status, allInvoices]);

  const handleDelete = async (invoiceId: string) => {
    const invoice = allInvoices.find(inv => inv.id === invoiceId);
    if (invoice) {
      setInvoiceToDelete(invoice);
      setDeleteModalOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (!invoiceToDelete) return;
    
    try {
      await invoiceService.deleteInvoice(invoiceToDelete.id);
      setAllInvoices(prev => prev.filter(inv => inv.id !== invoiceToDelete.id));
      toast.success('Invoice deleted successfully');
      setDeleteModalOpen(false);
      setInvoiceToDelete(null);
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setInvoiceToDelete(null);
  };

  const handleCreateInvoice = () => {
    setIsCreateModalOpen(true);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setIsEditModalOpen(true);
  };

  const handleSubmitInvoice = async (invoiceData: Invoice) => {
    setFormLoading(true);
    try {
      if (editingInvoice) {
        // Update existing invoice
        await invoiceService.updateInvoice(editingInvoice.id, invoiceData);
        setIsEditModalOpen(false);
        setEditingInvoice(null);
      } else {
        // Create new invoice
        await invoiceService.createInvoice(invoiceData);
        setIsCreateModalOpen(false);
      }
      // Reload invoices from the server
      await loadInvoices();
      toast.success('Invoice saved successfully');
    } catch (error) {
      console.error('Failed to save invoice:', error);
      toast.error('Failed to save invoice');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setEditingInvoice(null);
  };

  const canEditInvoice = (invoice: Invoice): boolean => {
    // Admins can always edit
    if (auth.user?.role === 'admin') return true;

    // Employees with canManageInvoices can edit any invoice
    if (auth.user?.permissions?.canManageInvoices) return true;

    // Employees without canManageInvoices:
    // Can only edit their own invoices created today
    const today = new Date().toLocaleDateString('en-CA');
    console.log("today:::::::::", today)
    return (
      invoice.user_id === auth.user?.id &&
      formatDate(invoice.invoice_date) === formatDate(today)
    );
  };

  const canDeleteInvoice = (invoice: Invoice): boolean => {
    // Admins can always edit
    if (auth.user?.role === 'admin') return true;

    // Employees with canManageInvoices can edit any invoice
    // if (auth.user?.permissions?.canManageInvoices) return true;

    // Employees without canManageInvoices:
    // Can only edit their own invoices created today
    const today = new Date().toLocaleDateString('en-CA');
    console.log("today:::::::::", today)
    return (
      invoice.user_id === auth.user?.id &&
      formatDate(invoice.invoice_date) === formatDate(today)
    );
  };

  const getStatusColor = (status: InvoiceStatus): string => {
    switch (status) {
      case 'paid': return styles.paid;
      case 'pending': return styles.pending;
      case 'overdue': return styles.overdue;
      case 'partially_paid': return styles.partiallyPaid;
      // case 'draft': return styles.draft;
      // case 'cancelled': return styles.cancelled;
      default: return styles.pending;
    }
  };

  const getStatusLabel = (status: InvoiceStatus): string => {
    switch (status) {
      case 'paid': return 'Paid';
      case 'pending': return 'Pending';
      case 'overdue': return 'Overdue';
      case 'partially_paid': return 'Partially Paid';
      // case 'draft': return 'Draft';
      // case 'cancelled': return 'Cancelled';
      default: return 'Pending';
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading invoices...</p>
      </div>
    );
  }

  return (
    <div className={styles.invoiceList}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h2>Invoices</h2>
          <p>Manage your invoice records</p>
        </div>
          <Button
            variant="primary"
            onClick={handleCreateInvoice}
          >
            Create Invoice
          </Button>
      </div>

      <div className={styles.controls}>
        <div className={styles.filters}>
           <div className={styles.searchContainer}>
            <div className={styles.searchWrapper}>
              <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                placeholder="Search by invoice number or customer..."
                value={filters.search || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className={styles.searchInput}
              />
              {filters && (
                <button
                  className={styles.clearButton}
                  onClick={() => setFilters({ search: '', status: undefined, from_date: '', to_date: '', date: '' })}
                  aria-label="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className={styles.dateInputWrapper}>
            <input
              type="date"
              value={filters.date || ''}
              max={new Date().toLocaleDateString('en-CA')}
              onChange={async e => {
                const date = e.target.value;
                setFilters(prev => ({ ...prev, date }));
                if (date) {
                  setLoading(true);
                  try {
                    const invoices = await invoiceService.getInvoicesByDate(date);
                    setAllInvoices(Array.isArray(invoices) ? invoices : []);
                    setFilteredInvoices(Array.isArray(invoices) ? invoices : []);
                  } catch (error) {
                    setAllInvoices([]);
                    setFilteredInvoices([]);
                  } finally {
                    setLoading(false);
                  }
                } else {
                  // If date is cleared, reload today's invoices
                  const today = new Date().toLocaleDateString('en-CA').slice(0, 10);
                  setLoading(true);
                  try {
                    const invoices = await invoiceService.getInvoicesByDate(today);
                    setAllInvoices(Array.isArray(invoices) ? invoices : []);
                    setFilteredInvoices(Array.isArray(invoices) ? invoices : []);
                  } catch (error) {
                    setAllInvoices([]);
                    setFilteredInvoices([]);
                  } finally {
                    setLoading(false);
                  }
                }
              }}
              className={styles.dateFilter}
              placeholder="Select date..."
            />
            <span className={styles.dateIcon}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
            {filters.date && (
              <button
                className={styles.clearDateBtn}
                onClick={async () => {
                  setFilters(prev => ({ ...prev, date: '' }));
                  const today = new Date().toLocaleDateString('en-CA').slice(0, 10);
                  setLoading(true);
                  try {
                    const invoices = await invoiceService.getInvoicesByDate(today);
                    setAllInvoices(Array.isArray(invoices) ? invoices : []);
                    setFilteredInvoices(Array.isArray(invoices) ? invoices : []);
                  } catch (error) {
                    setAllInvoices([]);
                    setFilteredInvoices([]);
                  } finally {
                    setLoading(false);
                  }
                }}
                type="button"
                aria-label="Clear date"
              >
                ×
              </button>
            )}
          </div>

          {/* <select
            value={filters.status || ''}
            onChange={(e) => setFilters(prev => ({ 
              ...prev, 
              status: e.target.value as InvoiceStatus || undefined 
            }))}
            className={styles.statusFilter}
          >
            <option value="">All Status</option> */}
            {/* <option value="draft">Draft</option> */}
            {/* <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="overdue">Overdue</option> */}
            {/* <option value="cancelled">Cancelled</option> */}
          {/* </select> */}
        </div>

      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Service Amount</th>
              <th>Total Amount</th>
              {/* <th>Received Amount</th>
              <th>Status</th> */}
              <th>Created By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.emptyState}>
                  {filters.search || filters.status || filters.from_date || filters.to_date || filters.date ? 'No invoices found matching your filters.' : 'No invoices found.'}
                </td>
              </tr>
            ) : (
              filteredInvoices.map(invoice => (
                <tr key={invoice.id}>
                  <td>
                    <div className={styles.invoiceNumber}>
                      <span className={styles.number}>{invoice.invoice_number}</span>
                    </div>
                  </td>
                  <td>{invoice.customer_name}</td>
                  <td>{formatDateTimeDMY(invoice.created_at)}</td>
                  <td>{formatCurrency(invoice.amount_total)}</td>
                  <td>{formatCurrency(invoice.service_total)}</td>
                  <td>{formatCurrency(invoice.total_amount)}</td>
                  {/* <td>{formatCurrency(invoice.partially_received_amounts)}</td> */}
                  {/* <td>
                    <span className={`${styles.status} ${getStatusColor(invoice.status)}`}>
                      {getStatusLabel(invoice.status)}
                    </span>
                  </td> */}
                  <td>{invoice.created_by}</td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionButton}
                        onClick={() => navigate(`/invoices/${invoice.id}`)}
                        title="View Invoice"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                      {canEditInvoice(invoice) && (
                        <button
                          className={styles.actionButton}
                          onClick={() => handleEditInvoice(invoice)}
                          title="Edit Invoice"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                      )}
                      {canDeleteInvoice(invoice) && (
                        <button
                          className={styles.actionButton}
                          onClick={() => handleDelete(invoice.id)}
                          title="Delete Invoice"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3,6 5,6 21,6"></polyline>
                            <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            variant="secondary"
            size="sm"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
          >
            Previous
          </Button>
          <span className={styles.pageInfo}>
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => prev + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create Invoice Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        title="Create New Invoice"
        size="xl"
      >
        <InvoiceForm
          existingInvoices={allInvoices}
          onSubmit={handleSubmitInvoice}
          onCancel={handleCloseModal}
          loading={formLoading}
        />
      </Modal>

      {/* Edit Invoice Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        title="Edit Invoice"
        size="xl"
      >
        <InvoiceForm
          invoice={editingInvoice || undefined}
          onSubmit={handleSubmitInvoice}
          onCancel={handleCloseModal}
          loading={formLoading}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={cancelDelete}
        title="Delete Invoice"
        size="md"
      >
        <div className={styles.deleteModalContent}>
          <div className={styles.deleteWarning}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.warningIcon}>
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <h3>Are you sure you want to delete this invoice?</h3>
            <p>This action cannot be undone. The invoice and all its data will be permanently removed.</p>
            
            {invoiceToDelete && (
              <div className={styles.invoiceDetails}>
                <p><strong>Invoice:</strong> {invoiceToDelete.invoice_number}</p>
                <p><strong>Customer:</strong> {invoiceToDelete.customer_name}</p>
                <p><strong>Amount:</strong> {formatCurrency(invoiceToDelete.total_amount)}</p>
              </div>
            )}
          </div>
          
          <div className={styles.deleteActions}>
            <Button
              variant="secondary"
              onClick={cancelDelete}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
            >
              Delete Invoice
            </Button>
          </div>
        </div>
      </Modal>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default InvoiceList; 
